import { 
    Typography, AppBar, Toolbar, Select, MenuItem, Button, Box, 
    Checkbox, IconButton, Tooltip, Chip, Divider 
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import StarIcon from '@mui/icons-material/Star';
import { IForecast } from "../../../interface/forecast";
import { fetchPostProtect } from "../../../fetchs/post";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { sessionEnd, showSnackbar } from "../../../redux/actions";
import { deleteOne } from "../../../fetchs/delete";
import AlerteDialog from "../../AlerteDialog/AlerteDialog";
import { useState } from "react";

const ForecastHeader = ({
    forecast,
    activeTab,
    forecastDates,
    forecastTabs,
    setActiveTab,
    version,
    setVersion,
    official,
    navigate,
    dispatch,
    setError,
    getForecast
}: any) => {
    const token = useSelector((state: RootState) => state.user.token);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // --- LOGIQUE API (Identique à ton code mais nettoyé) ---
    const duplicateVersion = async () => {
        if (!token || !forecast) return;
        const body = JSON.stringify({
            id: forecast._id,
            oldVersion: version,
            newVersion: forecast.versions[forecast.versions.length - 1].number + 1
        });

        try {
            const response = await fetchPostProtect(token, "/forecast/duplicateVersion", body);
            if (response.status === 200) {
                dispatch(showSnackbar("Version dupliquée !", "success"));
                getForecast();
            } else if (response.status === 401) {
                dispatch(sessionEnd());
            }
        } catch (err: any) { setError(err); }
    };

    const confirmDelete = async () => {
        if (!token || !forecast) return;
        const res = await deleteOne(token, "/forecast/version/", `${forecast._id}/${version}`);
        if (res.status === 200) {
            dispatch(showSnackbar("Version supprimée !", "success"));
            getForecast();
        } else if (res.status === 401) {
            dispatch(sessionEnd());
        }
        setShowDeleteDialog(false);
    };

    const changeOfficialVersion = async () => {
        if (!token || !forecast) return;
        const body = JSON.stringify({ id: forecast._id, version });
        const response = await fetchPostProtect(token, "/forecast/officialVersion", body);
        if (response.status === 200) {
            dispatch(showSnackbar("Nouvelle version officielle définie !", "success"));
            getForecast();
        }
    };

    const getForecastExportXml = async () => {
        if (!token || !forecast) return;
        try {
            const backEndUrl = window._env_.REACT_APP_BACKEND;
            const response = await fetch(`${backEndUrl}/forecast/${forecast._id}/${version}/export`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${forecast.name}_v${version}.xlsx`;
            a.click();
        } catch (error) { console.error(error); }
    };

    return (
        <AppBar position="sticky" sx={{ bgcolor: "#0f172a", backgroundImage: "none", boxShadow: "none", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
                
                {/* PARTIE GAUCHE : Retour et Titre */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton onClick={() => navigate("/prévisionnel")} sx={{ color: "white", mr: 2, bgcolor: "rgba(255,255,255,0.05)", '&:hover': { bgcolor: "rgba(255,255,255,0.15)" } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.5px", color: "rgba(255,255,255,0.7)" }}>
                                {forecast?.name || "Projet"}
                            </Typography>
                            {official && (
                                <Chip label="OFFICIEL" size="small" color="warning" icon={<StarIcon />} sx={{ fontWeight: 900, height: 20, fontSize: '0.6rem' }} />
                            )}
                        </Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 600 }}>
                            Période : {forecastDates}
                        </Typography>
                    </Box>
                </Box>

                {/* PARTIE CENTRALE : Sélecteur d'onglets (SÉLECTION UNIQUE) */}
                <Select
                    value={activeTab}
                    onChange={(e) => setActiveTab(e.target.value as string)}
                    sx={{ 
                        width: 300, 
                        height: 40, 
                        bgcolor: "rgba(255,255,255,0.05)", 
                        color: "white", 
                        borderRadius: 2,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.2)" },
                        "& .MuiSvgIcon-root": { color: "white" },
                        "& .MuiSelect-select": { display: 'flex', alignItems: 'center' }
                    }}
                    renderValue={(selected) => (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: "#FFD28E" }}>
                            Section : {selected}
                        </Typography>
                    )}
                >
                    {forecastTabs.map((tab: any, index: number) => (
                        <MenuItem 
                            key={index} 
                            value={tab.title}
                            sx={{ 
                                fontWeight: activeTab === tab.title ? 800 : 400,
                                color: activeTab === tab.title ? "primary.main" : "inherit"
                            }}
                        >
                            <Typography variant="body2">{tab.title}</Typography>
                        </MenuItem>
                    ))}
                </Select>

                {/* PARTIE DROITE : Versions et Actions */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", bgcolor: "rgba(255,255,255,0.05)", borderRadius: 2, px: 1.5, py: 0.5, border: "1px solid rgba(255,255,255,0.1)" }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: "rgba(255,255,255,0.7)", mr: 1.5 }}>V.</Typography>
                        <Select
                            variant="standard"
                            value={version}
                            onChange={(e) => setVersion(parseInt(e.target.value as string))}
                            disableUnderline
                            sx={{ color: "white", fontWeight: 800, minWidth: 40 }}
                        >
                            {forecast?.versions.map((v: any) => (
                                <MenuItem key={v.number} value={v.number} sx={{ fontWeight: v.official ? 800 : 400 }}>
                                    {v.number} {v.official ? "★" : ""}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ bgcolor: "rgba(255,255,255,0.1)", mx: 1 }} />

                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Exporter Excel">
                            <IconButton onClick={getForecastExportXml} sx={{ color: "#FFD28E" }}><FileDownloadIcon /></IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Dupliquer la version">
                            <IconButton onClick={duplicateVersion} sx={{ color: "#FFD28E" }}><ContentCopyIcon /></IconButton>
                        </Tooltip>

                        <Tooltip title="Définir comme officiel">
                            <span>
                                <IconButton onClick={changeOfficialVersion} disabled={official} sx={{ color: official ? "rgba(255,255,255,0.2)" : "#FFD28E" }}>
                                    <StarIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Supprimer la version">
                            <span>
                                <IconButton onClick={() => setShowDeleteDialog(true)} disabled={official} sx={{ color: official ? "rgba(255,255,255,0.2)" : "#f44336" }}>
                                    <DeleteForeverIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Toolbar>

            <AlerteDialog
                open={showDeleteDialog}
                handleClose={() => setShowDeleteDialog(false)}
                deleteProject={confirmDelete}
                title="Supprimer cette version ?"
                contentText="Attention, toutes les données de la version actuelle seront définitivement effacées."
                color="error"
            />
        </AppBar>
    );
};

export default ForecastHeader;